import { useEffect, useState } from "react";
import { ShieldCheck, Check, X, FloppyDisk, LockSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const ROLE_LABEL = { owner: "Owner", field_executive: "Field Executive", report_writer: "Report Writer", accountant: "Accountant" };

export default function RolesPermissions() {
  const [modules, setModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [access, setAccess] = useState({});
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = () => api.get("/permissions").then((r) => {
    setModules(r.data?.modules || []);
    setRoles(r.data?.roles || []);
    setAccess(r.data?.access || {});
    setDirty(false);
  }).catch(() => toast.error("Could not load permissions"));

  useEffect(() => { load(); }, []);

  const toggle = (moduleKey, role, locked) => {
    if (locked || role === "owner") return;
    setAccess((a) => ({ ...a, [moduleKey]: { ...(a[moduleKey] || {}), [role]: !a?.[moduleKey]?.[role] } }));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      modules.filter((m) => !m.locked).forEach((m) => { payload[m.key] = access[m.key] || {}; });
      const r = await api.put("/permissions", { access: payload });
      setAccess(r.data?.access || {});
      setDirty(false);
      toast.success("Access control matrix saved — enforced on the server immediately");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save permissions");
    } finally { setBusy(false); }
  };

  const Cell = ({ on, locked, role }) => (
    <span
      className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border transition-colors ${
        role === "owner"
          ? "bg-emerald-100 text-emerald-800 border-emerald-200"
          : on
            ? "bg-emerald-100 text-emerald-800 border-emerald-200"
            : "bg-slate-100 text-slate-500 border-slate-200"
      } ${locked || role === "owner" ? "opacity-70" : "cursor-pointer hover:ring-2 hover:ring-accent/40"}`}
    >
      {role === "owner" ? <Check size={10} weight="bold" /> : on ? <Check size={10} weight="bold" /> : <X size={10} weight="bold" />}
      {role === "owner" ? "Full" : on ? "Access" : "No access"}
    </span>
  );

  return (
    <div className="space-y-6" data-testid="roles-permissions-page">
      <PageHeader overline="Team" title="Roles & Permissions" subtitle="Toggle what each role can access — changes are enforced on the server" />

      <Card className="border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <div>
            <SectionTitle icon={ShieldCheck}>Access Control Matrix</SectionTitle>
            <p className="text-xs text-muted-foreground">Click a cell to grant or revoke a role's access to a module. Enforced on the server for every request.</p>
          </div>
          <Button onClick={save} disabled={busy || !dirty} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-permissions-btn">
            <FloppyDisk size={16} className="mr-1" /> {busy ? "Saving…" : "Save Changes"}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted">
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wide">Module</TableHead>
                {roles.map((r) => <TableHead key={r} className="text-xs uppercase tracking-wide text-center">{ROLE_LABEL[r] || r}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {modules.map((m) => (
                <TableRow key={m.key} className="hover:bg-muted/40" data-testid={`perm-row-${m.key}`}>
                  <TableCell className="text-sm text-primary">
                    <span className="inline-flex items-center gap-1.5">
                      {m.locked && <LockSimple size={13} className="text-muted-foreground" weight="fill" />}
                      {m.label}
                    </span>
                  </TableCell>
                  {roles.map((r) => (
                    <TableCell key={r} className="text-center">
                      <button type="button" onClick={() => toggle(m.key, r, m.locked)} disabled={m.locked || r === "owner"}
                        data-testid={`perm-cell-${m.key}-${r}`} className="bg-transparent border-0 p-0">
                        <Cell on={!!access?.[m.key]?.[r]} locked={m.locked} role={r} />
                      </button>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="px-5 py-3 border-t border-border">
          <p className="text-[11px] text-muted-foreground">
            <LockSimple size={11} className="inline mr-1" weight="fill" />
            Locked rows stay owner-only for security (Team, Records/Backup, GIS); <b>My Account</b> is always available to everyone. The Owner is always Full and cannot be changed.
          </p>
        </div>
      </Card>
    </div>
  );
}
